import { type RefObject, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { type Config, type Driver, type DriverHook } from 'driver.js';

import demoTours from '../../config/demo-tours.json';
import { generateScenarioKey, getElementsFromScenarios, translateScenarios } from './utils';
import { type Scenario, type ScenarioWithElements } from './types';
import { selectUserUID } from '../../store/selectors/userInfo';
import { type TranslationKey } from '../i18n/types';
import { useSelectFeatureSettings } from '../../apis/startup/startup.selectors';

// Delay before starting the tour, to let the highlighted element finish rendering/animating.
const TOUR_START_DELAY_MS = 400;

const checkElementsAvailability = (elements: string[]) => {
  if (!elements || elements.length === 0) {
    return false;
  }

  return elements.every(selector => document.querySelector(selector) !== null);
};

const scrollHighlightedElementIntoView: DriverHook = element => {
  element?.scrollIntoView({ behavior: 'auto', block: 'center' });
};

// Binds click event to the link inside the popover to trigger the corresponding action on the highlighted element.
const bindPopoverTargetLink: Config['onPopoverRender'] = (popover, { state }) => {
  popover.description.querySelector('[data-tour-open-target]')?.addEventListener('click', event => {
    event.preventDefault();
    state.activeElement?.querySelector<HTMLElement>('a, button')?.click();
  });
};

const createScenarioDriver = (
  driverFactory: (options?: Config) => Driver,
  selectedScenario: Scenario<string> | undefined,
  scenarioKey: string,
  activationCatalogProductSheet: boolean
): Driver | null => {
  const { needNewProductSheet = false } = selectedScenario || {};

  if (needNewProductSheet && !activationCatalogProductSheet) {
    return null;
  }

  return driverFactory({
    ...selectedScenario,
    onHighlightStarted: scrollHighlightedElementIntoView,
    onPopoverRender: bindPopoverTargetLink,
    onDestroyed: () => localStorage.setItem(scenarioKey, 'true'),
  });
};

const startScenarioTour = (activeDriver: Driver, elements: string[], scenariosRef: RefObject<Map<string, boolean>>, id: string) => {
  setTimeout(() => {
    if (checkElementsAvailability(elements)) {
      activeDriver.drive();
    } else {
      scenariosRef.current.set(id, false);
    }
  }, TOUR_START_DELAY_MS);
};

const processScenarioElement = (
  { id, elements }: ScenarioWithElements,
  driverFactory: (options?: Config) => Driver,
  scenarios: Scenario<TranslationKey>[],
  scenariosRef: RefObject<Map<string, boolean>>,
  userUniqueId: string,
  activationCatalogProductSheet: boolean
) => {
  if (!checkElementsAvailability(elements) || scenariosRef.current.get(id)) {
    return;
  }
  scenariosRef.current.set(id, true);

  const selectedScenario = scenarios.find(scenario => scenario.id === id);
  const scenarioKey = generateScenarioKey(id, userUniqueId);
  const activeDriver = createScenarioDriver(driverFactory, selectedScenario, scenarioKey, activationCatalogProductSheet);

  if (!activeDriver) {
    return;
  }

  startScenarioTour(activeDriver, elements, scenariosRef, id);
};

/**
 * Custom hook to manage driver.js scenarios with dynamic element observation.
 * This hook sets up an Mutation Observer to monitor the presence of
 * required elements in the DOM and starts the driver tour when all elements
 * are available.
 * If you want to add new scenarios, update the demo-tours.json file.
 */
export const useDemoTours = () => {
  const userUniqueId = useSelector(selectUserUID);
  const scenariosRef = useRef<Map<string, boolean>>(new Map());
  const activationCatalogProductSheet = useSelectFeatureSettings('activationCatalogProductSheet');

  const scenarios = translateScenarios(demoTours as Scenario<TranslationKey>[]);
  const scenariosWithElements = getElementsFromScenarios(scenarios, userUniqueId);

  useEffect(() => {
    // Skip driver.js initialization in Playwright testing environments
    if (globalThis.parent.Playwright) {
      return;
    }
    let mutationObserver: MutationObserver | null = null;

    if (scenariosWithElements.length > 0) {
      Promise.all([import('driver.js'), import('driver.js/dist/driver.css')]).then(([{ driver }]) => {
        mutationObserver = new MutationObserver(() => {
          scenariosWithElements.forEach(scenarioWithElements =>
            processScenarioElement(scenarioWithElements, driver, scenarios, scenariosRef, userUniqueId, activationCatalogProductSheet)
          );
        });

        mutationObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });
      });
    }

    return () => {
      mutationObserver?.disconnect();
    };
  }, [scenariosWithElements]);
};
